import { Component, Input, Output, OnDestroy, OnInit, OnChanges, SimpleChanges, EventEmitter, Inject } from '@angular/core';
import {
  Observable,
  SubscriptionLike,
  of,
  throwError,
  ReplaySubject,
  switchMap,
  map,
  catchError,
  delay,
  last,
  finalize,
  tap,
  takeUntil,
} from 'rxjs';
import { FINGERPRINT_MATCH_THRESHOLD, FINGERPRINT_QUALITY_LIMIT_FOR_MATCH } from 'types';
import { AppContext } from '../../base/control/context';
import { FingerPrintsService, FingerPrintsServiceFactory, FingerPrintServiceResponse } from '../../service/finger-prints.service';
import { HWServiceConfig } from '../../service/hw-service-config';
import { BioInfoTemplate, FingerPosition, FingerData } from '../../service/hw-service-data';
import { FpScannerInfo } from '../../service/hw-service-model';
import { FingerLocalization } from './fingerprint-context';

const FINGER_POSITIONS = [
  FingerPosition.RightLittle,
  FingerPosition.RightRing,
  FingerPosition.RightMiddle,
  FingerPosition.RightIndex,
  FingerPosition.RightThumb,

  FingerPosition.LeftThumb,
  FingerPosition.LeftIndex,
  FingerPosition.LeftMiddle,
  FingerPosition.LeftRing,
  FingerPosition.LeftLittle,
];

const DEFAULT_FINGER_INDEX = -1;

enum FingerScanState {
  NotScanned,
  Scanning,
  Scanned,
  Skipped,
}

enum ScannerState {
  Unknown,
  Available,
  Unvailable,
}

class FingerState {
  public get image() {
    return this._data!.data;
  }

  public get data(): FingerData {
    return this._data!;
  }

  public get handLocalized() {
    return FingerLocalization.handName(this.hand);
  }

  public get fingerNameLocalized() {
    return FingerLocalization.fingerName(this.fingerName);
  }

  public get hand() {
    return this._data?.position.split(' ')[0];
  }

  public get fingerName() {
    return this._data?.position.split(' ')[1];
  }

  public get selected() {
    return this._selected;
  }

  public get scanState() {
    return this._scanState;
  }

  public get error() {
    return this._error;
  }

  private _data: FingerData | null;
  private _selected: boolean;
  private _error: boolean;
  private _scanState: FingerScanState;

  constructor(
    fingerData: FingerData | null,
    selected: boolean,
    state: FingerScanState = FingerScanState.NotScanned,
    error: boolean = false,
  ) {
    this._data = fingerData;
    this._selected = selected;
    this._scanState = state;
    this._error = error;
  }

  public toScanning() {
    return new FingerState(this._data, this._selected, FingerScanState.Scanning, this._error);
  }

  public toScanned() {
    return new FingerState(this._data, this._selected, FingerScanState.Scanned, this._error);
  }

  public toSkipped() {
    return new FingerState(this._data, this._selected, FingerScanState.Skipped, this._error);
  }

  public withError(error: boolean) {
    return new FingerState(this._data, this._selected, this._scanState, error);
  }
}

export class PassFingerprintMessage {
  error: boolean;
  message: string;

  constructor(message: string, error: boolean = false) {
    this.error = error;
    this.message = message;
  }
}

export class FingerprintsVerifyResult {
  valid: boolean;
  invalidFingers: InvalidFingers[];

  constructor(valid: boolean, invalidFingers: InvalidFingers[]) {
    this.valid = valid;
    this.invalidFingers = invalidFingers;
  }
}

interface InvalidFingers {
  data: FingerData;
  scanState: FingerScanState;
}

@Component({
  selector: 'i-pass-fp-verify',
  templateUrl: './pass.fp.verify.component.html',
  styleUrls: ['./pass.fp.verify.component.scss'],
  providers: [{ provide: FingerPrintsServiceFactory, useValue: { create: (c: HWServiceConfig) => new FingerPrintsService(c) } }],
})
export class PassFpVerifyComponent implements OnInit, OnChanges, OnDestroy {
  FingerScanState = FingerScanState;
  ScannerState = ScannerState;

  @Input()
  fingerprintData: BioInfoTemplate[];

  @Output()
  aborted = new EventEmitter<FingerprintsVerifyResult>();

  @Output()
  finished = new EventEmitter<FingerprintsVerifyResult>();

  @Output()
  progressMessages = new EventEmitter<PassFingerprintMessage>();

  @Output()
  restartRequier = new EventEmitter<void>();

  public fingers: FingerState[] = [];
  public currentFinger: FingerState;
  public inProgress: boolean = false;
  public scannerState: ScannerState;

  private fpService: FingerPrintsService;
  private scanners: FpScannerInfo[];
  private currentFingerIndex: number = DEFAULT_FINGER_INDEX;
  private scanningSub: SubscriptionLike;
  private dead = new ReplaySubject<void>();

  constructor(
    private appContext: AppContext,
    private config: HWServiceConfig,
    private fpServiceFactory: FingerPrintsServiceFactory,
    @Inject(FINGERPRINT_QUALITY_LIMIT_FOR_MATCH) public qualityLimit: number,
    @Inject(FINGERPRINT_MATCH_THRESHOLD) public matchThreshold: number,
  ) {}

  ngOnInit(): void {
    this.scannerState = ScannerState.Unknown;
    this.fpService = this.fpServiceFactory.create(this.config);
    this.fpService
      .connect$()
      .pipe(
        switchMap(() => this.fpService.GetScannersList()),
        takeUntil(this.dead),
      )
      .subscribe(
        (r) => {
          if (r.length === 0) {
            this.scannerState = ScannerState.Unvailable;
            this.emitProgressMessage('Сканер недоступен', true);
            return;
          }
          this.scannerState = ScannerState.Available;
          // TODO: что делать если их несколько?
          this.scanners = r;
          setTimeout(() => {
            // Выбираем первый палец
            this.skipOrNext();
          });
        },
        (e) => {
          this.scannerState = ScannerState.Unvailable;
          this.appContext.getMessageSink()('ERROR', 'fingerprints.error.connection');
          this.emitProgressMessage('Сканер недоступен', true);
        },
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fingerprintData']) {
      this.fillFingers();
      this.processFingerprintData(changes['fingerprintData'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.dead.next();
    this.dead.complete();
    this.cancelScan();
    this.fpService.disconnect();
  }

  public skipOrNext(skipScannedFingers: boolean = false): void {
    this.cancelScan();

    if (skipScannedFingers) {
      this.setCurrentFingerState(
        this.currentFinger.scanState === FingerScanState.Scanned ? this.currentFinger : this.currentFinger.toSkipped(),
      );
    }

    const wasLast = this.nextFinger(skipScannedFingers);

    if (wasLast) {
      this.emitResult(this.finished);
    } else {
      this.scanningSub = this.startFingerScanSequence$()
        .pipe(takeUntil(this.dead))
        .subscribe((r) => {
          if (r) {
            this.setCurrentFingerState(this.currentFinger.toScanned().withError(false));
          } else {
            this.setCurrentFingerState(this.currentFinger.withError(true));
            this.emitProgressMessage('Отпечаток не совпадает');
          }
        });
    }
  }

  public repeat(): void {
    this.setCurrentFingerState(
      this.currentFinger.scanState === FingerScanState.Scanned ? this.currentFinger : this.currentFinger.withError(false),
    );
    this.cancelScan();
    this.scanningSub = this.startFingerScanSequence$()
      .pipe(takeUntil(this.dead))
      .subscribe((r) => {
        if (r) {
          this.setCurrentFingerState(this.currentFinger.toScanned().withError(false));
        } else {
          this.setCurrentFingerState(this.currentFinger.withError(true));
          this.emitProgressMessage('Отпечаток не совпадает');
        }
      });
  }

  public restartScanning() {
    this.cancelScan();
    this.restartRequier.emit();
  }

  public close(): void {
    this.emitResult(this.aborted);
  }

  private cancelScan(): void {
    !this.scanningSub?.closed && this.scanningSub?.unsubscribe();
  }

  private fillFingers(): void {
    const newFingers = [];

    for (let i = 0; i < 10; i++) {
      newFingers[i] = new FingerState(null, false);
    }

    this.fingers = newFingers;
  }

  private processFingerprintData(data: BioInfoTemplate[]): void {
    for (let template of data) {
      const templateFingers = template['bio-data-block']!.fingers;

      for (let finger of templateFingers) {
        const headerPos = template['bio-header-template'].finger;
        const imagePos = finger.position;
        if (headerPos !== imagePos) {
          console.warn(`Не совпадают данные о позиции отпечатка пальца HeaderPos=${headerPos} ImagePos=${imagePos}`);
        }
        this.fingers[FINGER_POSITIONS.indexOf(imagePos)] = new FingerState(finger, true);
      }
    }
  }

  private nextFinger(skipScannedFingers: boolean = false): boolean {
    if (this.currentFinger) {
      this.currentFinger = this.currentFinger.scanState !== FingerScanState.Scanned ? this.currentFinger.toSkipped() : this.currentFinger;
    }

    const findIndex = () =>
      this.fingers.findIndex((v, i) => {
        if (i > this.currentFingerIndex && v.selected) {
          if (skipScannedFingers && v.scanState === FingerScanState.Scanned) {
            return false;
          }
          return true;
        } else {
          return false;
        }
      });

    let index = findIndex();
    let wasLastFinger = false;

    // Все доступные пальцы были отсканированы. Ищем по-кругу неотсканированные
    if (index === -1) {
      this.currentFingerIndex = DEFAULT_FINGER_INDEX;
      index = findIndex();
      wasLastFinger = true;
    }

    // Неотсканированных пальцев не осталось
    if (index === -1) {
      return wasLastFinger;
    }

    this.currentFinger = this.fingers[index];
    this.currentFinger =
      this.currentFinger.scanState !== FingerScanState.Scanned && this.currentFinger.scanState !== FingerScanState.Skipped
        ? this.currentFinger.toScanning()
        : this.currentFinger;
    this.fingers[index] = this.currentFinger;
    this.currentFingerIndex = index;

    return wasLastFinger;
  }

  private startFingerScanSequence$(): Observable<boolean> {
    return this.startCapture$().pipe(
      delay(1),
      switchMap((r) => {
        if (r === null) {
          return of(false);
        } else {
          return this.startCompare$(r).pipe(
            tap(() => {
              this.inProgress = true;
            }),
          );
        }
      }),
      finalize(() => {
        this.inProgress = false;
      }),
    );
  }

  private startCapture$(): Observable<FingerPrintServiceResponse | null> {
    let scanner: FpScannerInfo;
    if (this.scanners.length == 2) {
      scanner = this.scanners[this.currentFingerIndex < 5 ? 0 : 1];
    } else {
      scanner = this.scanners[0];
    }
    return this.fpService.ScanFinger1(scanner!.id!, this.qualityLimit).pipe(
      last(),
      switchMap((r) => {
        if (r === 'stop_bad_quality!') {
          // ???
          this.appContext.getMessageSink()('ERROR', 'fingerprints.error.badquality');
          return throwError('stop_bad_quality!');
        } else if (r instanceof FingerPrintServiceResponse) {
          return of(r);
        }

        return of(null);
      }),
      catchError((e) => {
        this.appContext.getMessageSink()('ERROR', 'fingerprints.error.scaning');
        this.emitProgressMessage('Отпечаток не считан');
        return of(null);
      }),
    );
  }

  private startCompare$(fp: FingerPrintServiceResponse): Observable<boolean> {
    const chipData = this.currentFinger.image;
    const scanData = fp.image;

    return this.fpService.Compare(chipData!, scanData).pipe(
      map((r) => r >= this.matchThreshold),
      catchError((e) => {
        this.appContext.getMessageSink()('ERROR', 'fingerprints.error.compare');
        this.emitProgressMessage('Отпечаток не совпадает');
        return of(false);
      }),
    );
  }

  private setCurrentFingerState(state: FingerState): void {
    this.currentFinger = state;
    this.fingers[this.currentFingerIndex] = this.currentFinger;
  }

  private emitProgressMessage(message: string, error: boolean = false): void {
    this.progressMessages.emit(new PassFingerprintMessage(message, error));
  }

  private emitResult(emitter: EventEmitter<FingerprintsVerifyResult>): void {
    emitter.emit(
      new FingerprintsVerifyResult(
        this.fingers.filter((x) => x.selected).every((x) => x.scanState === FingerScanState.Scanned && !x.error),
        this.fingers
          .filter((x) => x.selected && (x.scanState !== FingerScanState.Scanned || x.error))
          .map((x) => ({ data: x.data, scanState: x.scanState })),
      ),
    );
  }
}
